import { ChatsCircle } from '@phosphor-icons/react'
import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import '../chat.css'

import { ErrorState } from '@/components/error-state'
import { LoadingState } from '@/components/loading-state'
import { EmptyState } from '@/features/discovery/components/empty-state'
import { PullToRefresh } from '@/features/discovery/components/pull-to-refresh'
import { useIsDesktop } from '@/features/discovery/hooks/use-is-desktop'

import { chatApi, type PrivateConversation } from '../api'
import { ChatConfirmDialog } from '../components/chat-confirm-dialog'
import { ChatNotice, type ChatNoticeState } from '../components/chat-notice'
import {
  ChatRefreshButton,
  ChatRefreshStatus,
} from '../components/chat-refresh'
import { ChatRow } from '../components/chat-row'
import { chatErrorKey } from '../format'
import { useChatLists } from '../hooks/use-chat-lists'
import { conversationDraftKey, useChatUiStore } from '../state/chat-ui-store'

/**
 * Legacy `/messages` destination kept for deep links (`UI_PARITY` §1). The
 * primary entry point is `/chats`; this screen lists only private
 * conversations and reuses the same row/menu/confirmation behavior.
 */
export function PrivateMessagesScreen() {
  const { t } = useTranslation()
  const isDesktop = useIsDesktop()
  const {
    privateChats,
    phase,
    refreshing,
    error,
    refresh,
    setPrivateChats,
  } = useChatLists({ includeActivityChats: false })
  const [notice, setNotice] = useState<ChatNoticeState | null>(null)
  const [pendingPin, setPendingPin] = useState<Set<string>>(() => new Set())
  const [deleteTarget, setDeleteTarget] =
    useState<PrivateConversation | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)

  async function togglePin(conversation: PrivateConversation) {
    if (pendingPin.has(conversation.id)) return
    setPendingPin((current) => new Set(current).add(conversation.id))
    const next = !conversation.pinned
    setPrivateChats((items) =>
      items.map((item) =>
        item.id === conversation.id ? { ...item, pinned: next } : item,
      ),
    )
    try {
      await chatApi.pinPrivateConversation(conversation.id, next)
      setPrivateChats((items) =>
        items.map((item) =>
          item.id === conversation.id ? { ...item, pinned: next } : item,
        ),
      )
      setNotice({
        messageKey: next ? 'chat.notice.pinned' : 'chat.notice.unpinned',
        tone: 'success',
      })
    } catch (cause) {
      setPrivateChats((items) =>
        items.map((item) =>
          item.id === conversation.id
            ? { ...item, pinned: conversation.pinned }
            : item,
        ),
      )
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    } finally {
      setPendingPin((current) => {
        const nextSet = new Set(current)
        nextSet.delete(conversation.id)
        return nextSet
      })
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || deleteBusy) return
    setDeleteBusy(true)
    try {
      await chatApi.hidePrivateConversation(deleteTarget.id)
      useChatUiStore.getState().clearDraft(conversationDraftKey(deleteTarget.id))
      setPrivateChats((items) =>
        items.filter((item) => item.id !== deleteTarget.id),
      )
      setDeleteTarget(null)
      setNotice({ messageKey: 'chat.notice.privateHidden', tone: 'success' })
    } catch (cause) {
      setNotice({ messageKey: chatErrorKey(cause), tone: 'error' })
    } finally {
      setDeleteBusy(false)
    }
  }

  return (
    <main
      id="main-content"
      tabIndex={-1}
      aria-labelledby="messages-title"
      className="content-width pt-[var(--safe-top)] pb-10"
    >
      <div className="flex flex-wrap items-center gap-2 py-4">
        <Link
          to="/chats"
          className="mr-auto text-sm font-bold text-teal hover:underline"
        >
          {t('chat.private.backToChats')}
        </Link>
        {isDesktop ? (
          <ChatRefreshButton onRefresh={refresh} refreshing={refreshing} />
        ) : null}
      </div>

      <h1 id="messages-title" className="text-[clamp(2rem,4vw,3rem)]">
        {t('screens.messages.title')}
      </h1>

      <ChatNotice notice={notice} onDismiss={() => setNotice(null)} />
      {phase === 'ready' && error ? (
        <p className="chat-notice chat-notice--error" role="alert">
          {t(chatErrorKey(error, {privateChat: true}))}
        </p>
      ) : null}

      <PullToRefresh
        className="mt-4"
        onRefresh={refresh}
        refreshing={refreshing}
        disabled={isDesktop}
      >
        <ChatRefreshStatus visible={refreshing} />
        {phase === 'loading' ? (
          <LoadingState labelKey="chat.private.loadingList" />
        ) : phase === 'error' ? (
          <ErrorState error={error} onRetry={refresh} />
        ) : privateChats.length === 0 ? (
          <EmptyState
            icon={<ChatsCircle size={28} weight="bold" aria-hidden="true" />}
            titleKey="chat.private.emptyListTitle"
            bodyKey="chat.private.emptyList"
          />
        ) : (
          <ul className="chat-list">
            {privateChats.map((conversation) => {
              return (
                <ChatRow
                  key={conversation.id}
                  item={{
                    kind: 'private',
                    id: conversation.id,
                    pinned: conversation.pinned,
                    lastMessageAt: conversation.lastMessageAt,
                    conversation,
                  }}
                  pinned={conversation.pinned}
                  unreadCount={conversation.unreadCount}
                  onPin={() => void togglePin(conversation)}
                  onDelete={() => setDeleteTarget(conversation)}
                  busy={pendingPin.has(conversation.id)}
                />
              )
            })}
          </ul>
        )}
      </PullToRefresh>

      <ChatConfirmDialog
        open={deleteTarget !== null}
        titleKey="chat.confirm.private.title"
        bodyKey="chat.confirm.private.body"
        confirmLabelKey="chat.confirm.delete"
        busy={deleteBusy}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={() => void confirmDelete()}
      />
    </main>
  )
}
